import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { RawMaterialWarehouse } from '../../../raw-material-inventory/raw-material.model';
import { GenericBase } from '../../../common/helpers/generic';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { EmployeeBasicInfo, UserRole } from '../../../employees/employees.model';
import { finalize, Subscription } from 'rxjs';
import { EmployeesService } from '../../../employees/employees.service';
import { RawMaterialService } from '../../../raw-material-inventory/raw-material.service';
import { NO_WAREHOUSES_ALERT_MSG } from '../../../common/helpers/constants';
import { DeleteConfirmationModalComponent } from '../../../common/components/delete-confirmation-modal/delete-confirmation-modal.component';

import { Action, TableColumn } from '../../../common/models/common.model';
import { AuthService } from '../../../authentication/auth.service';
import { User } from '../../../authentication/auth.models';

@Component({
  selector: 'app-warehouses',
  standalone: false,
  templateUrl: './warehouses.component.html',
  styleUrl: './warehouses.component.scss',
})
export class WarehousesComponent extends GenericBase implements OnInit, OnDestroy {
  @ViewChild('updateWarehouseContent') updateWarehouseContent!: TemplateRef<any>;
  @ViewChild('addWarehouseContent') addWarehouseContent!: TemplateRef<any>;
  @Input() wareHouses: RawMaterialWarehouse[] = [];

  protected readonly columns: TableColumn<RawMaterialWarehouse>[] = [
    { label: 'Name', field: 'name' },
    { label: 'Plant Head', field: 'plant_head.user.full_name', format: val => val || 'Not Assigned' },
  ];
  protected readonly actions = [Action.EDIT, Action.DELETE];

  protected productionManagers: EmployeeBasicInfo[] = [];
  protected modalRef!: NgbModalRef;
  protected currentWarehouse: RawMaterialWarehouse | null = null;
  protected selectedProductionManagerId: string | null = null;
  protected isUpdatingWarehouse: boolean = false;
  protected isProductionManagersDataLoading: boolean = false;

  protected editWarehouseName: string = '';
  protected editWarehouseAddress: string = '';

  protected newWarehouseName: string = '';
  protected newWarehouseAddress: string = '';
  protected isAddingWarehouse: boolean = false;

  protected currentUser: User | null = null;

  private ngbModal!: NgbModal;
  private productionManagerSubscription!: Subscription;

  constructor(
    modalService: NgbModal,
    private employeeService: EmployeesService,
    private rawMaterialService: RawMaterialService,
    private authService: AuthService
  ) {
    super(modalService);
    this.ngbModal = modalService;
  }

  ngOnInit(): void {
    this.currentUser = this.authService.getCurrentUser();
    this.getProductionManagersList();
  }

  protected openUpdateWarehouseModal(warehouse: RawMaterialWarehouse): void {
    this.currentWarehouse = warehouse;
    this.editWarehouseName = warehouse.name;
    this.editWarehouseAddress = warehouse.address || '';
    this.selectedProductionManagerId = warehouse.plant_head ? warehouse.plant_head.id : null;

    const modalOptions: NgbModalOptions = {
      size: 'md',
      backdrop: 'static',
    };

    this.modalRef = this.openModal(this.updateWarehouseContent, modalOptions);
  }

  protected onDeleteWarehouse(warehouse: RawMaterialWarehouse): void {
    const ref = this.ngbModal.open(DeleteConfirmationModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    ref.componentInstance.title = 'Delete Warehouse';
    ref.componentInstance.message = `Are you sure you want to delete "${warehouse.name}"?`;
    ref.componentInstance.warning = 'This action cannot be undone.';

    ref.result.then(() => {
      this.rawMaterialService.delete_warehouse(warehouse.id).subscribe({
        next: () => {
          this.wareHouses = this.wareHouses.filter(w => w.id !== warehouse.id);
        },
      });
    });
  }

  protected openAddWarehouseModal(): void {
    this.newWarehouseName = '';
    this.newWarehouseAddress = '';

    const modalOptions: NgbModalOptions = {
      size: 'md',
      backdrop: 'static',
    };

    this.modalRef = this.openModal(this.addWarehouseContent, modalOptions);
  }

  protected onAddWarehouse(): void {
    if (!this.newWarehouseName) return;

    this.isAddingWarehouse = true;

    const payload = {
      name: this.newWarehouseName,
      address: this.newWarehouseAddress,
    };

    this.rawMaterialService
      .post_warehouse(payload)
      .pipe(finalize(() => (this.isAddingWarehouse = false)))
      .subscribe({
        next: (newWarehouse: RawMaterialWarehouse) => {
          this.wareHouses.push(newWarehouse);
          this.modalRef?.dismiss();
        },
      });
  }

  protected onUpdateWarehouse(): void {
    if (!this.currentWarehouse || !this.editWarehouseName) return;

    this.isUpdatingWarehouse = true;

    const payload: Partial<{ name: string; address: string; plant_head_id: string }> = {
      name: this.editWarehouseName,
      address: this.editWarehouseAddress,
    };

    if (this.selectedProductionManagerId) {
      payload['plant_head_id'] = this.selectedProductionManagerId;
    }

    this.productionManagerSubscription = this.rawMaterialService
      .update_warehouse(this.currentWarehouse.id, payload)
      .pipe(finalize(() => (this.isUpdatingWarehouse = false)))
      .subscribe({
        next: (updatedWarehouse: RawMaterialWarehouse) => {
          const idx = this.wareHouses.findIndex(w => w.id === updatedWarehouse.id);
          if (idx !== -1) this.wareHouses[idx] = updatedWarehouse;
          this.modalRef?.dismiss();
        },
      });
  }

  private getProductionManagersList(): void {
    this.isProductionManagersDataLoading = true;
    this.productionManagerSubscription = this.employeeService
      .lighter_list({ exclude_team: 'sales' })
      .pipe(finalize(() => (this.isProductionManagersDataLoading = false)))
      .subscribe({
        next: (employees: EmployeeBasicInfo[]) => {
          this.productionManagers = employees;
        },
      });
  }

  ngOnDestroy(): void {
    this.modalRef?.dismiss();
    this.productionManagerSubscription?.unsubscribe();
  }

  protected readonly NO_WAREHOUSES_ALERT_MSG = NO_WAREHOUSES_ALERT_MSG;
  protected readonly UserRole = UserRole;
}
